import { IMetricsService } from './interfaces/IMetricsService';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import type { Counter, Histogram, ObservableGauge, Meter } from '@opentelemetry/api';

export interface MetricsConfig {
  collectorUrl?: string;
  serviceName?: string;
  serviceVersion?: string;
  debug?: boolean;
  customerId?: string;
  tenantId?: string;
  environment?: string;
  exportIntervalMillis?: number;
}

export class MetricsService implements IMetricsService {
  private meterProvider: MeterProvider | null = null;
  private meter: Meter | null = null;
  private readonly collectorUrl: string;
  private readonly serviceName: string;
  private readonly serviceVersion: string;
  private readonly debug: boolean;
  private readonly customerId?: string;
  private readonly tenantId?: string;
  private readonly environment?: string;
  private readonly exportIntervalMillis: number;
  private metricReader: PeriodicExportingMetricReader | null = null;

  // Business Metrics - Counters
  private captureCounter: Counter | null = null;
  private errorCounter: Counter | null = null;
  private modelLoadCounter: Counter | null = null;
  private userInteractionCounter: Counter | null = null;

  // Business Metrics - Histograms (for latencies and sizes)
  private captureLatencyHistogram: Histogram | null = null;
  private modelLoadLatencyHistogram: Histogram | null = null;
  private detectionLatencyHistogram: Histogram | null = null;
  private imageSizeHistogram: Histogram | null = null;

  // Business Metrics - Gauges (for current state)
  private activeSessionsGauge: ObservableGauge | null = null;
  private memoryUsageGauge: ObservableGauge | null = null;

  // State tracking
  private activeSessions: number = 0;
  private gaugeValues: Map<string, number> = new Map();

  constructor(config: MetricsConfig = {}) {
    this.collectorUrl = config.collectorUrl || 'https://collector.jaak.ai/v1/metrics';
    this.serviceName = config.serviceName || 'jaak-stamps-webcomponent';
    this.serviceVersion = config.serviceVersion || '2.1.0';
    this.debug = config.debug || false;
    this.customerId = config.customerId;
    this.tenantId = config.tenantId;
    this.environment = config.environment || 'production';
    this.exportIntervalMillis = config.exportIntervalMillis || 60000; // Export every 60 seconds
  }

  initialize(): void {
    try {
      // Create OTLP metrics exporter
      const exporter = new OTLPMetricExporter({
        url: this.collectorUrl,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Create periodic exporting metric reader
      this.metricReader = new PeriodicExportingMetricReader({
        exporter: exporter,
        exportIntervalMillis: this.exportIntervalMillis,
        exportTimeoutMillis: 30000,
      });

      // Create meter provider
      this.meterProvider = new MeterProvider({
        readers: [this.metricReader],
      });

      // Get meter
      this.meter = this.meterProvider.getMeter(this.serviceName, this.serviceVersion);

      // Initialize business metrics
      this.initializeMetrics();

      if (this.debug) {
        console.log('[MetricsService] Initialized successfully', {
          collectorUrl: this.collectorUrl,
          serviceName: this.serviceName,
          serviceVersion: this.serviceVersion,
          environment: this.environment,
          customerId: this.customerId,
          tenantId: this.tenantId,
          exportIntervalMillis: this.exportIntervalMillis,
        });
      }
    } catch (error) {
      console.error('[MetricsService] Failed to initialize:', error);
      // Don't throw - metrics should fail silently in production
    }
  }

  private initializeMetrics(): void {
    if (!this.meter) return;

    const baseAttributes = this.getBaseAttributes();

    try {
      // COUNTERS - Track events and totals

      // Total captures performed
      this.captureCounter = this.meter.createCounter('jaak.captures.total', {
        description: 'Total number of document captures',
        unit: '1',
      });

      // Total errors
      this.errorCounter = this.meter.createCounter('jaak.errors.total', {
        description: 'Total number of errors',
        unit: '1',
      });

      // Model loads
      this.modelLoadCounter = this.meter.createCounter('jaak.model.loads.total', {
        description: 'Total number of model loads',
        unit: '1',
      });

      // User interactions
      this.userInteractionCounter = this.meter.createCounter('jaak.user.interactions.total', {
        description: 'Total user interactions (clicks, etc.)',
        unit: '1',
      });

      // HISTOGRAMS - Track distributions of latencies and sizes

      // Capture latency (time from start to completion)
      this.captureLatencyHistogram = this.meter.createHistogram('jaak.capture.duration', {
        description: 'Duration of capture operations',
        unit: 'ms',
      });

      // Model load latency
      this.modelLoadLatencyHistogram = this.meter.createHistogram('jaak.model.load.duration', {
        description: 'Duration of model loading',
        unit: 'ms',
      });

      // Detection latency (per frame)
      this.detectionLatencyHistogram = this.meter.createHistogram('jaak.detection.duration', {
        description: 'Duration of detection inference',
        unit: 'ms',
      });

      // Image sizes
      this.imageSizeHistogram = this.meter.createHistogram('jaak.image.size', {
        description: 'Size of captured images',
        unit: 'bytes',
      });

      // GAUGES - Track current state values

      // Active capture sessions
      this.activeSessionsGauge = this.meter.createObservableGauge('jaak.sessions.active', {
        description: 'Number of active capture sessions',
        unit: '1',
      });

      this.activeSessionsGauge.addCallback((observableResult) => {
        observableResult.observe(this.activeSessions, baseAttributes);
      });

      // Memory usage (if available)
      if ('memory' in performance) {
        this.memoryUsageGauge = this.meter.createObservableGauge('jaak.memory.usage', {
          description: 'JavaScript heap memory usage',
          unit: 'bytes',
        });

        this.memoryUsageGauge.addCallback((observableResult) => {
          const memInfo = (performance as any).memory;
          if (memInfo) {
            observableResult.observe(memInfo.usedJSHeapSize, baseAttributes);
          }
        });
      }

      if (this.debug) {
        console.log('[MetricsService] Business metrics initialized');
      }
    } catch (error) {
      console.error('[MetricsService] Failed to initialize metrics:', error);
    }
  }

  private getBaseAttributes(): Record<string, any> {
    const attributes: Record<string, any> = {
      'service.name': this.serviceName,
      'service.version': this.serviceVersion,
      'deployment.environment': this.environment,
    };

    if (this.customerId) {
      attributes['customer.id'] = this.customerId;
    }

    if (this.tenantId) {
      attributes['tenant.id'] = this.tenantId;
    }

    return attributes;
  }

  incrementCounter(name: string, value: number = 1, attributes?: Record<string, any>): void {
    try {
      const baseAttrs = this.getBaseAttributes();
      const allAttrs = { ...baseAttrs, ...attributes };

      // Use predefined counters or create ad-hoc
      switch (name) {
        case 'captures':
          this.captureCounter?.add(value, allAttrs);
          break;
        case 'errors':
          this.errorCounter?.add(value, allAttrs);
          break;
        case 'model_loads':
          this.modelLoadCounter?.add(value, allAttrs);
          break;
        case 'user_interactions':
          this.userInteractionCounter?.add(value, allAttrs);
          break;
        default:
          // Create ad-hoc counter if needed
          if (this.meter) {
            const counter = this.meter.createCounter(`jaak.${name}.total`, {
              description: `Total ${name}`,
              unit: '1',
            });
            counter.add(value, allAttrs);
          }
      }

      if (this.debug) {
        console.log(`[MetricsService] Counter incremented: ${name} += ${value}`, attributes);
      }
    } catch (error) {
      console.error('[MetricsService] Failed to increment counter:', error);
    }
  }

  recordHistogram(name: string, value: number, attributes?: Record<string, any>): void {
    try {
      const baseAttrs = this.getBaseAttributes();
      const allAttrs = { ...baseAttrs, ...attributes };

      // Use predefined histograms
      switch (name) {
        case 'capture_duration':
          this.captureLatencyHistogram?.record(value, allAttrs);
          break;
        case 'model_load_duration':
          this.modelLoadLatencyHistogram?.record(value, allAttrs);
          break;
        case 'detection_duration':
          this.detectionLatencyHistogram?.record(value, allAttrs);
          break;
        case 'image_size':
          this.imageSizeHistogram?.record(value, allAttrs);
          break;
        default:
          // Create ad-hoc histogram if needed
          if (this.meter) {
            const histogram = this.meter.createHistogram(`jaak.${name}`, {
              description: `Distribution of ${name}`,
              unit: 'ms',
            });
            histogram.record(value, allAttrs);
          }
      }

      if (this.debug) {
        console.log(`[MetricsService] Histogram recorded: ${name} = ${value}`, attributes);
      }
    } catch (error) {
      console.error('[MetricsService] Failed to record histogram:', error);
    }
  }

  setGauge(name: string, value: number, attributes?: Record<string, any>): void {
    try {
      // Store gauge value for observable callbacks
      this.gaugeValues.set(name, value);

      // Update known gauges
      if (name === 'active_sessions') {
        this.activeSessions = value;
      }

      if (this.debug) {
        console.log(`[MetricsService] Gauge set: ${name} = ${value}`, attributes);
      }
    } catch (error) {
      console.error('[MetricsService] Failed to set gauge:', error);
    }
  }

  async measureDuration<T>(name: string, fn: () => T | Promise<T>, attributes?: Record<string, any>): Promise<T> {
    const startTime = performance.now();

    try {
      const result = await fn();
      const duration = performance.now() - startTime;

      this.recordHistogram(name, duration, attributes);

      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      this.recordHistogram(name, duration, { ...attributes, error: true });
      throw error;
    }
  }

  async flush(): Promise<void> {
    try {
      if (this.metricReader) {
        await this.metricReader.forceFlush();
      }

      if (this.debug) {
        console.log('[MetricsService] Metrics flushed');
      }
    } catch (error) {
      console.error('[MetricsService] Failed to flush metrics:', error);
    }
  }

  async cleanup(): Promise<void> {
    try {
      if (this.metricReader) {
        await this.metricReader.forceFlush();
        await this.metricReader.shutdown();
      }

      if (this.meterProvider) {
        await this.meterProvider.shutdown();
      }

      if (this.debug) {
        console.log('[MetricsService] Cleaned up successfully');
      }
    } catch (error) {
      console.error('[MetricsService] Failed to cleanup:', error);
    }
  }

  // Helper methods for common business metrics

  /**
   * Record a successful capture
   */
  recordCapture(side: 'front' | 'back', mode: 'auto' | 'manual', durationMs: number): void {
    this.incrementCounter('captures', 1, { side, mode });
    this.recordHistogram('capture_duration', durationMs, { side, mode });
  }

  /**
   * Record an error
   */
  recordError(errorType: string, operation: string): void {
    this.incrementCounter('errors', 1, { error_type: errorType, operation });
  }

  /**
   * Record model load performance
   */
  recordModelLoad(modelType: 'detection' | 'classification', durationMs: number, cached: boolean): void {
    this.incrementCounter('model_loads', 1, { model_type: modelType, cached });
    this.recordHistogram('model_load_duration', durationMs, { model_type: modelType, cached });
  }

  /**
   * Record detection performance
   */
  recordDetection(durationMs: number, detectionsFound: number): void {
    this.recordHistogram('detection_duration', durationMs, { detections_found: detectionsFound });
  }

  /**
   * Record image size
   */
  recordImageSize(side: 'front' | 'back', sizeBytes: number): void {
    this.recordHistogram('image_size', sizeBytes, { side });
  }

  /**
   * Update active sessions count
   */
  updateActiveSessions(count: number): void {
    this.setGauge('active_sessions', count);
  }

  /**
   * Record user interaction
   */
  recordUserInteraction(interactionType: string): void {
    this.incrementCounter('user_interactions', 1, { interaction_type: interactionType });
  }
}
